import { useState, useEffect } from 'react';
import { getToken } from '@shared/services/api';
import { fetchShared, invalidateShared, SHARED_KEYS } from '@shared/services/sharedResource';
import i18n from '@/i18n';

interface User {
  id: string;
  email: string;
  full_name: string;
  email_verified: boolean;
  role?: string;
  is_platform_admin?: boolean;
  timezone?: string;
  language?: string;
  is_suspended?: boolean;
  created_at?: string;
  last_login_at?: string;
  organization?: {
    id: string;
    name: string;
  };
}

interface JWTPayload {
  user_id: string;
  email: string;
  full_name: string;
  exp: number;
}

// Normalize language codes from backend to frontend format
function normalizeLanguageCode(lang: string): string {
  const languageMap: Record<string, string> = {
    pt: 'pt-BR',
    'pt-br': 'pt-BR',
    pt_BR: 'pt-BR',
    es: 'es',
    en: 'en',
  };
  return languageMap[lang.toLowerCase()] || lang;
}

export function useUser() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(false);
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    // Shared with SuspensionBanner and the compliance pages, which each fetch
    // the same endpoint — one request per page load instead of four.
    fetchShared(SHARED_KEYS.profile, async () => {
      const res = await fetch('/api/v1/auth/profile', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) throw new Error('Failed to fetch profile');
      return res.json();
    })
      .then((data) => {
        setUser(data.user);
        // Apply user's language preference to i18n
        if (data.user.language) {
          const normalizedLang = normalizeLanguageCode(data.user.language);
          if (i18n.language !== normalizedLang) {
            i18n.changeLanguage(normalizedLang).catch(() => {
              // Silently ignore language change errors
            });
          }
        }
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        // Fall back to JWT decoding
        try {
          const parts = token.split('.');
          if (parts.length !== 3) throw new Error('Invalid token');

          const payload = JSON.parse(window.atob(parts[1])) as JWTPayload;
          if (payload.exp * 1000 < Date.now()) {
            setUser(null);
            setLoading(false);
            return;
          }

          setUser({
            id: payload.user_id,
            email: payload.email,
            full_name: payload.full_name,
            email_verified: true,
          });
          setLoading(false);
        } catch (_decodeError) {
          setUser(null);
          setLoading(false);
        }
      });
  }, [attempt]);

  const retry = () => {
    invalidateShared(SHARED_KEYS.profile);
    setAttempt((value) => value + 1);
  };
  return { user, loading, error, retry };
}
